import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import './App.css';
import Header from './components/Header';
import CalculatorForm from './components/CalculatorForm';
import Analytics from './components/Analytics';
import CalculatorGuide from './components/CalculatorGuide';
import DownloadPDF from './components/DownloadPDF';
import UserAuth from './components/UserAuth';
import UserDashboard from './components/UserDashboard';
import AdminAuth from './components/AdminAuth';
import AdminDashboard from './components/AdminDashboard';
import SafeIcon from './common/SafeIcon';

const {FiSettings} = FiIcons;

function App() {
  // Form state
  const [formData, setFormData] = useState({
    revenue: '',
    foodCosts: '',
    laborCosts: '',
    rentUtilities: '',
    otherExpenses: ''
  });

  // Period type (daily, weekly, monthly)
  const [periodType, setPeriodType] = useState('daily');

  // Profit calculation state
  const [profitData, setProfitData] = useState({
    daily: 0,
    weekly: 0,
    monthly: 0,
    margin: 0
  });

  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserAuth, setShowUserAuth] = useState(false);
  const [showUserDashboard, setShowUserDashboard] = useState(false);

  // Admin state
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);

  // Downloads tracking
  const [downloads, setDownloads] = useState([]);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('current_user');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('current_user');
      }
    }

    // Load downloads data from localStorage
    const savedDownloads = localStorage.getItem('profit_downloads');
    if (savedDownloads) {
      try {
        setDownloads(JSON.parse(savedDownloads));
      } catch (error) {
        console.error('Error parsing saved downloads:', error);
        localStorage.removeItem('profit_downloads');
        setDownloads([]);
      }
    }
  }, []);

  // Calculate profit whenever form data or period type changes
  useEffect(() => {
    // Convert values based on period type
    let revenue = parseFloat(formData.revenue) || 0;
    let foodCosts = parseFloat(formData.foodCosts) || 0;
    let laborCosts = parseFloat(formData.laborCosts) || 0;
    let rentUtilities = parseFloat(formData.rentUtilities) || 0;
    let otherExpenses = parseFloat(formData.otherExpenses) || 0;

    // Convert to daily values if needed
    if (periodType === 'weekly') {
      revenue = revenue / 7;
      foodCosts = foodCosts / 7;
      laborCosts = laborCosts / 7;
      rentUtilities = rentUtilities / 7;
      otherExpenses = otherExpenses / 7;
    } else if (periodType === 'monthly') {
      revenue = revenue / 30;
      foodCosts = foodCosts / 30;
      laborCosts = laborCosts / 30;
      rentUtilities = rentUtilities / 30;
      otherExpenses = otherExpenses / 30;
    }

    // Calculate total costs
    const totalCosts = foodCosts + laborCosts + rentUtilities + otherExpenses;

    // Calculate daily profit
    const dailyProfit = revenue - totalCosts;

    // Calculate weekly and monthly profit
    const weeklyProfit = dailyProfit * 7;
    const monthlyProfit = dailyProfit * 30;

    // Calculate profit margin
    const margin = revenue > 0 ? (dailyProfit / revenue) * 100 : 0;

    // Update profit data state
    setProfitData({
      daily: dailyProfit,
      weekly: weeklyProfit,
      monthly: monthlyProfit,
      margin: margin
    });
  }, [formData, periodType]);

  // Handle user login
  const handleUserLogin = (user) => {
    setCurrentUser(user);
    setShowUserAuth(false);
  };

  // Handle user logout
  const handleUserLogout = () => {
    localStorage.removeItem('current_user');
    setCurrentUser(null);
    setShowUserDashboard(false);
  };

  // Handle admin login
  const handleAdminLogin = (isAuthenticated) => {
    if (isAuthenticated) {
      setShowAdminAuth(false);
      setShowAdminDashboard(true);
    }
  };

  // Handle download
  const handleDownload = (downloadData) => {
    const newDownloads = [...downloads, downloadData];
    setDownloads(newDownloads);
    localStorage.setItem('profit_downloads', JSON.stringify(newDownloads));
  };

  // Handle admin access button click
  const handleAdminAccess = () => {
    setShowAdminAuth(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Admin Access Button */}
      <div className="fixed top-4 right-4 z-40">
        <motion.button
          whileHover={{scale: 1.05}}
          whileTap={{scale: 0.95}}
          onClick={handleAdminAccess}
          className="p-3 bg-gradient-to-r from-slate-600 to-slate-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          title="Admin Dashboard"
        >
          <SafeIcon icon={FiSettings} className="text-lg" />
        </motion.button>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <Header
          currentUser={currentUser}
          onLoginClick={() => setShowUserAuth(true)}
          onDashboardClick={() => setShowUserDashboard(true)}
        />

        {/* Calculator Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Left Column */}
          <div className="space-y-8">
            <CalculatorForm
              formData={formData}
              setFormData={setFormData}
              periodType={periodType}
              setPeriodType={setPeriodType}
            />
            <CalculatorGuide />
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            <Analytics
              profitData={profitData}
              formData={formData}
              periodType={periodType}
            />
            <DownloadPDF
              profitData={profitData}
              formData={formData}
              onDownload={handleDownload}
              currentUser={currentUser}
              onLoginRequest={() => setShowUserAuth(true)}
            />
          </div>
        </div>
      </div>

      {/* User Authentication Modal */}
      {showUserAuth && (
        <UserAuth
          onLogin={handleUserLogin}
          onClose={() => setShowUserAuth(false)}
        />
      )}

      {/* User Dashboard Modal */}
      {showUserDashboard && currentUser && (
        <UserDashboard
          user={currentUser}
          onLogout={handleUserLogout}
        />
      )}

      {/* Admin Authentication Modal */}
      {showAdminAuth && (
        <AdminAuth
          onLogin={handleAdminLogin}
          onClose={() => setShowAdminAuth(false)}
        />
      )}

      {/* Admin Dashboard Modal */}
      {showAdminDashboard && (
        <AdminDashboard
          downloads={downloads}
          onClose={() => setShowAdminDashboard(false)}
        />
      )}
    </div>
  );
}

export default App;